import React from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import CheckboxInput from '@divvy-web/skylab.checkboxinput'
import Spinner from '@divvy-web/skylab.spinner'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import { useApolloQueryErrorHandling } from '@sputnik/apollo/useApolloQueryErrorHandling'
import PrivacyPolicyLink from '@sputnik/components/PrivacyPolicyLink'
import TermsAndConditionsLink from '@sputnik/components/TermsAndConditionsLink'
import { useGetDsaAccessTokenLazyQuery } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/ApplicationTerms/GetDsaAccessToken.gql'
import { GetCreditApplicationTilaInfo_creditApplication_CreditApplication as CreditApplicationType } from '@sputnik/pages/PostApplicationPages/ApplicationAgreements/GetCreditApplicationTilaInfo.gql'
import { URL } from '@sputnik/resources/constants'

import styles from './styles.module.scss'

const {
  DSA_TERMS_WEX_BO,
  DSA_TERMS_CRB_BO,
  DSA_TERMS_CRB_JS,
  DSA_TERMS_WEX_JS,
  DSA_TERMS_WEBBANK_BO,
  DSA_TERMS_WEBBANK_PG,
} = URL

const termsMapper = {
  major_account_change: {
    business_only: DSA_TERMS_WEX_BO,
    crb_business_only: DSA_TERMS_CRB_BO,
    crb_j_s: DSA_TERMS_CRB_JS,
    crb_mc_j_s: DSA_TERMS_CRB_JS,
    crb_mc_business_only: DSA_TERMS_CRB_BO,
    j_s: DSA_TERMS_WEX_JS,
    wex_business_only: DSA_TERMS_WEX_BO,
    wex_j_s: DSA_TERMS_WEX_JS,
    web_bank_pg: DSA_TERMS_WEBBANK_PG,
    web_bank_business_only: DSA_TERMS_WEBBANK_BO,
  },
  migration: {
    business_only: DSA_TERMS_CRB_BO,
    crb_business_only: DSA_TERMS_CRB_BO,
    crb_j_s: DSA_TERMS_CRB_JS,
    crb_mc_j_s: DSA_TERMS_CRB_JS,
    crb_mc_business_only: DSA_TERMS_CRB_BO,
    j_s: DSA_TERMS_CRB_JS,
    web_bank_pg: DSA_TERMS_WEBBANK_PG,
    web_bank_business_only: DSA_TERMS_WEBBANK_BO,
  },
  new: {
    business_only: DSA_TERMS_WEX_BO,
    crb_business_only: DSA_TERMS_CRB_BO,
    crb_j_s: DSA_TERMS_CRB_JS,
    crb_mc_j_s: DSA_TERMS_CRB_JS,
    crb_mc_business_only: DSA_TERMS_CRB_BO,
    j_s: DSA_TERMS_WEX_JS,
    wex_business_only: DSA_TERMS_WEX_BO,
    wex_j_s: DSA_TERMS_WEX_JS,
    web_bank_pg: DSA_TERMS_WEBBANK_PG,
    web_bank_business_only: DSA_TERMS_WEBBANK_BO,
  },
}

interface OfferTermsCheckboxProps {
  creditApplication: CreditApplicationType
  hasAgreedToTerms: boolean
  onCheckboxClick: (e: React.ChangeEvent) => void
}

const OfferTermsCheckbox = ({ hasAgreedToTerms, creditApplication, onCheckboxClick }: OfferTermsCheckboxProps) => {
  const [getClassName, makeTestId] = useNameFormatter('OfferTermsCheckbox')
  const { dsaVersion, recordType, salesforceCreditId } = creditApplication || {}
  const termLink = dsaVersion && recordType && `${termsMapper[recordType][dsaVersion]}`

  const [getDsaAccessToken, { loading: isLoadingDsaAccessToken, error }] = useGetDsaAccessTokenLazyQuery({
    variables: { creditApplicationId: salesforceCreditId },
  })

  useApolloQueryErrorHandling({
    error,
    logging: {
      eventName: 'OfferTermsCheckbox',
      message: 'Error getting DSA access token to generate DSA document',
      action: 'getDsaAccessToken',
      attributes: {
        creditAppId: salesforceCreditId,
      },
    },
  })

  if (!creditApplication || isLoadingDsaAccessToken) return <Spinner centered />

  const handleCreditTermsClick = async () => {
    const { data } = await getDsaAccessToken()

    const generatedDsaDocumentUrl = `${window.__DivvyEnvironment.ONBOARDING_BACKEND}/dsa/${salesforceCreditId}?dsa_token=${data?.dsaAccessToken?.dsaToken}`
    window.open(generatedDsaDocumentUrl, '_blank', 'noopener, noreferrer')
  }

  return (
    <div
      className={`${styles[getClassName('')]} fs-unmask`}
      data-testid={makeTestId('')}
    >
      <CheckboxInput
        checked={hasAgreedToTerms}
        dataTestId={makeTestId('checkbox')}
        isSmall={false}
        label={
          <FormattedMessage
            defaultMessage='I have read and agree to the following: {creditTermsLink}, {termsAndConditionsLink}, {privacyPolicyLink}, {achLink}'
            id='sputnik.OfferTermsCheckbox__CgR8ko'
            values={{
              creditTermsLink: (
                <TextLink
                  className={getClassName('creditTerms-link')}
                  dataTestId={makeTestId('creditTerms-link')}
                  size={TextLinkSizes.REGULAR}
                  onClick={handleCreditTermsClick}
                >
                  <FormattedMessage
                    defaultMessage='Credit terms'
                    id='sputnik.OfferTermsCheckbox__bz/EmN'
                  />
                </TextLink>
              ),
              termsAndConditionsLink: <TermsAndConditionsLink dataTestId={makeTestId('terms-and-conditions-link')} />,
              privacyPolicyLink: <PrivacyPolicyLink dataTestId={makeTestId('privacy-policy-link')} />,
              achLink: (
                <TextLink
                  className={getClassName('ach-auth-link')}
                  dataTestId={makeTestId('ach-auth-link')}
                  href={termLink || ''}
                  size={TextLinkSizes.REGULAR}
                  target='_blank'
                >
                  <FormattedMessage
                    defaultMessage='ACH authorization'
                    id='sputnik.OfferTermsCheckbox__BQ+Tma'
                  />
                </TextLink>
              ),
            }}
          />
        }
        name='applicationAgreementTerms'
        onChange={onCheckboxClick}
      />
    </div>
  )
}

export default OfferTermsCheckbox
